import {
  CCS_PLANS,
  HALES_PLANS,
  CCS_COMPARISON_DATA,
  HALES_COMPARISON_DATA,
  type ComparisonRow,
  type Plan,
} from '@/lib/plans/climate-data';

export type BrandKey = 'climate' | 'hales' | 'abraham';

export interface BrandHero {
  headline: string;
  subheadline: string;
  urgencyLine: string;
  ctaText: string;
  badge?: string;
}

export interface BrandInfo {
  key: BrandKey;
  companyId: string;
  name: string;
  phone: string; // digits only
  phoneDisplay: string;
  since: string;
  serviceArea: string;
  yearsInBusiness: number;
  tagline: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  logoUrl: string;
  // Services offered
  hasPlumbing: boolean;
  hasAppliances: boolean;
  // Hero content
  hero: BrandHero;
  // Available tiers (Hales only has 2)
  availableTiers: ('standard' | 'premier' | 'optimum')[];
}

const BRAND_INFO: Record<BrandKey, BrandInfo> = {
  climate: {
    key: 'climate',
    companyId: 'climate',
    name: 'Climate Control Services',
    phone: '5616804467',
    phoneDisplay: '(561) 680-4467',
    since: '1973',
    serviceArea: 'Palm Beach County',
    yearsInBusiness: 53,
    tagline: 'Call. Chill. Smile.',
    colors: {
      primary: '#0066CC',
      secondary: '#00A9E0',
      accent: '#FF6B35',
    },
    logoUrl: '/brands/climate-logo.png',
    hasPlumbing: true,
    hasAppliances: true,
    hero: {
      headline: 'Tired of Surprise AC Repairs Destroying Your Budget?',
      subheadline: 'The Comfort Club gives you priority service, discounted repairs, and annual tune-ups that keep your system running — all for less than a Netflix subscription.',
      urgencyLine: 'Join 30,000+ Florida homeowners who never worry about AC again.',
      ctaText: 'See If You Qualify',
      badge: '50 YEARS SERVING PALM BEACH COUNTY',
    },
    availableTiers: ['standard', 'premier', 'optimum'],
  },
  hales: {
    key: 'hales',
    companyId: 'hales-ac',
    name: 'Hales AC',
    phone: '7278557140',
    phoneDisplay: '(727) 855-7140',
    since: '1986',
    serviceArea: 'St. Petersburg / Tampa Bay',
    yearsInBusiness: 40,
    tagline: 'Hales Yeah!',
    colors: {
      primary: '#005DB8',
      secondary: '#F4601B',
      accent: '#FF7A00',
    },
    logoUrl: '/brands/hales-logo.png',
    // CRITICAL: Hales is HVAC ONLY - no plumbing, water heaters, or appliances
    hasPlumbing: false,
    hasAppliances: false,
    hero: {
      headline: 'Tired of Surprise AC Repairs Destroying Your Budget?',
      subheadline: 'Join 30,000+ families who never worry about AC breakdowns. Get priority service, $0 diagnostics, and real repair coverage — all for less than a Netflix subscription.',
      urgencyLine: '⏰ Limited spots available — sign up today and start saving!',
      ctaText: 'See If You Qualify',
      badge: '40 YEARS SERVING TAMPA BAY',
    },
    // CRITICAL: Hales only has 2 tiers - NO Optimum tier
    availableTiers: ['standard', 'premier'],
  },
  abraham: {
    key: 'abraham',
    companyId: 'abraham-ac',
    name: 'Abraham AC',
    phone: '9543357659',
    phoneDisplay: '(954) 335-7659',
    since: '2003',
    serviceArea: 'Broward County',
    yearsInBusiness: 23,
    tagline: 'The President of HVAC!',
    colors: {
      primary: '#0193CF',
      secondary: '#F4601B',
      accent: '#FF7A00',
    },
    logoUrl: '/brands/abraham-logo.png',
    hasPlumbing: true,
    hasAppliances: true,
    hero: {
      headline: 'Tired of Surprise AC Repairs Destroying Your Budget?',
      subheadline: 'The President of HVAC gives you executive treatment: priority service, $0 diagnostics, and real coverage for your AC, plumbing, AND appliances.',
      urgencyLine: 'Join 30,000+ Broward County homeowners who never worry about home repairs again.',
      ctaText: 'See If You Qualify',
      badge: 'THE PRESIDENT OF HVAC',
    },
    availableTiers: ['standard', 'premier', 'optimum'],
  },
};

export function detectBrandFromHost(host?: string): BrandKey {
  const h = String(host ?? (typeof window !== 'undefined' ? window.location.hostname : '')).toLowerCase();

  // Match production domains + local dev hosts (e.g. hales.localhost / memberships.halesac.com).
  if (h.includes('hales')) return 'hales';
  if (h.includes('abraham')) return 'abraham';

  return 'climate';
}

export function getBrandInfo(brand: BrandKey): BrandInfo {
  return BRAND_INFO[brand];
}

export function getPlansForBrand(brand: BrandKey): Plan[] {
  const info = BRAND_INFO[brand];
  const colorPalette = [info.colors.primary, info.colors.secondary, info.colors.accent];
  
  // Use brand-specific plan arrays
  const basePlans = brand === 'hales' ? HALES_PLANS : CCS_PLANS;
  
  return basePlans.map((plan, idx) => ({
    ...plan,
    // Override colors with brand colors
    color: colorPalette[idx] || plan.color,
    // Add brand suffix to ID
    id: `${plan.code}-${info.companyId}`,
  }));
}

export function getPlanComparisonDataForBrand(brand: BrandKey): ComparisonRow[] {
  return brand === 'hales' ? HALES_COMPARISON_DATA : CCS_COMPARISON_DATA;
}

export interface PlanCardComparisonItem {
  text: string;
  tooltip?: string;
}

export interface PlanCardComparisonSection {
  title: string;
  items: PlanCardComparisonItem[];
}

export function getPlanCardComparisonSections({
  comparisonData,
  planCode,
  maxItemsPerSection = 2,
  maxTotalItems = 24,
}: {
  comparisonData: ComparisonRow[];
  planCode: Plan['code'];
  maxItemsPerSection?: number;
  maxTotalItems?: number;
}): PlanCardComparisonSection[] {
  const sections: PlanCardComparisonSection[] = [];
  let current: PlanCardComparisonSection | null = null;

  for (const row of comparisonData) {
    if (row.isSection) {
      current = { title: row.feature, items: [] };
      sections.push(current);
      continue;
    }

    const val = row[planCode];
    if (val === false || val === undefined || val === null) continue;

    const text = typeof val === 'string' ? `${row.feature}: ${val}` : row.feature;

    if (!current) {
      current = { title: 'Highlights', items: [] };
      sections.push(current);
    }

    current.items.push({ text, tooltip: row.tooltip });
  }

  const nonEmpty = sections.filter((s) => s.items.length > 0);
  const limited: PlanCardComparisonSection[] = [];
  let total = 0;

  for (const section of nonEmpty) {
    if (total >= maxTotalItems) break;
    const remaining = Math.max(0, maxTotalItems - total);
    const items = section.items.slice(0, Math.min(maxItemsPerSection, remaining));
    if (!items.length) continue;
    limited.push({ title: section.title, items });
    total += items.length;
  }

  return limited;
}

/**
 * Check if a brand supports plumbing/appliance services
 */
export function brandSupportsPlumbing(brand: BrandKey): boolean {
  return BRAND_INFO[brand].hasPlumbing;
}

export function brandSupportsAppliances(brand: BrandKey): boolean {
  return BRAND_INFO[brand].hasAppliances;
}


